import { Core } from "@faire/design-tokens";
import { logAssert } from "@faire/web/common/logging";
import { Column } from "@faire/web/slate/Layout";
import { getSpacing } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { NoTranslate } from "@faire/web/ui/lib/localization";
import { useMemo } from "react";
import React from "react";
import styled from "styled-components";

import { LoadableTypography } from "@faire/retailer/components/GhostLoading/LoadableTypography";
import { ILeanProductTile } from "@faire/retailer/features/MarketplaceItems/MarketplaceProduct/LeanProtos/ILeanProductTile";
import { MarketplaceProductBrandLink } from "@faire/retailer/features/MarketplaceItems/MarketplaceProductBrandLink";
import * as RetailerPaths from "@faire/retailer/paths";

export const LoggedOutBrandDetails = ({
  brand,
  onBrandLinkClick,
  generateBrandUrl,
  isProductLoaded,
}: {
  brand: ILeanProductTile.IBrand;
  brandRatingValue: string | undefined;
  brandRatingCount: number | undefined;
  productToken: string | undefined;
  onBrandLinkClick: (event: React.MouseEvent<HTMLAnchorElement>) => void;
  generateBrandUrl?: (brandToken: string) => string | undefined;
  isProductLoaded: boolean;
}) => {
  logAssert(!!brand, "Expect brand to be defined");
  logAssert(
    !!brand.token,
    "Expect brand.token to be defined given brand is defined"
  );
  logAssert(
    !!brand.name,
    "Expect brand.name to be defined given brand is defined"
  );

  const brandUrl = useMemo(() => {
    if (!brand.token) {
      return;
    }

    return generateBrandUrl
      ? generateBrandUrl(brand.token)
      : RetailerPaths.brandPage(brand.token);
  }, [brand.token, generateBrandUrl]);

  return (
    <>
      <BrandDetailsColumn>
        <BrandLink to={brandUrl} token={brand.token} onClick={onBrandLinkClick}>
          <BrandName isLoaded={isProductLoaded}>
            <NoTranslate>{brand.name}</NoTranslate>
          </BrandName>
        </BrandLink>
      </BrandDetailsColumn>
    </>
  );
};

const BrandDetailsColumn = styled(Column)`
  padding: ${getSpacing("2x")};
`;

const BrandLink = styled(MarketplaceProductBrandLink)`
  width: 100%;
  height: 100%;
  color: ${Core.text.default};

  ${responsiveMediaQueries.canHover} {
    &:hover {
      text-decoration: underline;
    }
  }
`;

const BrandName = styled(LoadableTypography).attrs({
  variant: "paragraphSansMedium",
  loadingInsetBottom: 2,
  color: "inherit",
})`
  overflow: hidden;
  text-overflow: ellipsis;

  @supports (-webkit-line-clamp: 1) {
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 1;
    -webkit-box-orient: vertical;
  }
`;
